import { guideContentByKey, resolveGuideContent } from "./guideContent.js";
import { faqCategories } from "./faqContent.js";
import {
  CHANNEL_TYPES,
  CHANNEL_ROUTING,
  PLATFORM_WORKFLOWS,
} from "./channelTypesContent.js";
import {
  CHAT_SCREEN_OVERVIEW,
  CHAT_AREAS,
  CHAT_WORKFLOWS,
} from "./chatScreenContent.js";
import { faqItemMatchesSegment, navDefMatchesSegment } from "./helpSegment.js";
import { WorkspaceSegments, normalizeWorkspaceSegment } from "../../constants/workspaceSegments.js";

const helpNavMainKeysBase = [
  { res: "dashboard", textKey: "nav.dashboard" },
  { res: "chat", textKey: "nav.chat" },
  { res: "sales_pipeline", textKey: "nav.salesPipeline" },
  { res: "clients", textKey: "nav.clients" },
  { res: "surveys", textKey: "nav.surveys", label: "Pesquisas" },
  { res: "awareness", textKey: "nav.awareness" },
  { res: "products", textKey: "nav.products" },
  { res: "campaigns", textKey: "nav.campaigns", label: "Campanhas" },
];

/** A visibilidade final é filtrada pelos scopes efetivos retornados pela API. */
export const helpNavMainKeys = Object.freeze(helpNavMainKeysBase);

const helpNavSettingsKeysBase = [
  { res: "companies", textKey: "nav.companies" },
  { res: "users", textKey: "nav.users" },
  { res: "positions", textKey: "nav.positions" },
  { res: "channels", textKey: "nav.channels" },
  { res: "types", textKey: "nav.types" },
  { res: "cities", textKey: "nav.passengerCities", segments: [WorkspaceSegments.PassengerTransport] },
  { res: "chatbot", textKey: "nav.chatbots" },
  { res: "agents", textKey: "nav.agents" },
  { res: "integrations", textKey: "nav.integrations" },
  { res: "paid_ads", textKey: "nav.paidAds" },
  { res: "workspace_i18n", textKey: "nav.workspaceLanguage" },
  { res: "automation_rules", textKey: "nav.automationRules" },
  { res: "antifraud_methods", textKey: "nav.antifraudMethods" },
  { res: "payment_methods", textKey: "nav.paymentMethods" },
  { res: "delivery_methods", textKey: "nav.deliveryMethods" },
  { res: "client_tags", textKey: "nav.clientTags" },
  { res: "categories", textKey: "nav.categories" },
  { res: "coupons", textKey: "nav.coupons" },
  { res: "banners", textKey: "nav.banners" },
  { res: "contents", textKey: "nav.contents" },
  { res: "awareness_stages", textKey: "nav.awarenessStages" },
];

export const helpNavSettingsKeys = Object.freeze(helpNavSettingsKeysBase);

export const HELP_TAB_KEYS = Object.freeze({
  guide: 0,
  faq: 1,
  news: 2,
});

const TAB_BY_NAME = Object.freeze({
  guide: HELP_TAB_KEYS.guide,
  guia: HELP_TAB_KEYS.guide,
  faq: HELP_TAB_KEYS.faq,
  news: HELP_TAB_KEYS.news,
  novidades: HELP_TAB_KEYS.news,
  changelog: HELP_TAB_KEYS.news,
});

export function tabKeyFromIndex(index) {
  if (index === HELP_TAB_KEYS.faq) return "faq";
  if (index === HELP_TAB_KEYS.news) return "news";
  return "guide";
}

export function tabIndexFromKey(key) {
  const k = String(key || "").trim().toLowerCase();
  return TAB_BY_NAME[k] ?? HELP_TAB_KEYS.guide;
}

/** Monta URL da Central de Ajuda (paths relativos — funcionam no SPA). */
export function buildHelpUrl({
  tab = "guide",
  section = "main",
  area = "",
  faqCat = "",
  faqItem = "",
  q = "",
} = {}) {
  const params = new URLSearchParams();
  const tabName = tabKeyFromIndex(typeof tab === "number" ? tab : tabIndexFromKey(tab));
  if (tabName !== "guide") params.set("tab", tabName);
  if (section && section !== "main") params.set("section", section);
  if (area) params.set("area", area);
  if (faqCat) params.set("cat", faqCat);
  if (faqItem !== "" && faqItem != null) params.set("item", String(faqItem));
  if (q) params.set("q", q);
  const qs = params.toString();
  return qs ? `/help?${qs}` : "/help";
}

export function parseHelpDeepLink(searchParams) {
  const sp = searchParams instanceof URLSearchParams ? searchParams : new URLSearchParams(searchParams || "");
  const tab = tabIndexFromKey(sp.get("tab") || "guide");
  const section = String(sp.get("section") || "main").trim() || "main";
  const area = String(sp.get("area") || "").trim();
  const faqCat = String(sp.get("cat") || "").trim();
  const faqItemRaw = sp.get("item");
  const faqItem = faqItemRaw != null && faqItemRaw !== "" ? Number(faqItemRaw) : null;
  const q = String(sp.get("q") || "").trim();
  return { tab, section, area, faqCat, faqItem, q };
}

export function helpAnchorId({ section = "main", area = "", faqCat = "", faqItem = null } = {}) {
  if (faqCat) {
    if (faqItem != null && !Number.isNaN(faqItem)) return `help-faq-${faqCat}-${faqItem}`;
    return `help-faq-${faqCat}`;
  }
  if (area) return `help-area-${section}-${area}`;
  if (section === "settings") return "help-nav-settings";
  if (section === "topbar") return "help-nav-topbar";
  return "help-nav-main";
}

function formatGuideArticle(guide) {
  if (!guide) return "";
  const steps = Array.isArray(guide.steps) ? guide.steps.map((s, i) => `${i + 1}. ${s}`).join("\n") : "";
  const tips = Array.isArray(guide.tips) ? guide.tips.map((t) => `- ${t}`).join("\n") : "";
  return [guide.what || "", steps ? `Como usar:\n${steps}` : "", tips ? `Observações:\n${tips}` : ""]
    .filter(Boolean)
    .join("\n\n");
}

function guideTopicsForSegment(segment) {
  const seg = normalizeWorkspaceSegment(segment);
  const topics = [];

  const pushSection = (sectionKey, defs) => {
    defs.forEach((def) => {
      if (!navDefMatchesSegment(def, seg)) return;
      const guide = resolveGuideContent(sectionKey, def.res, seg);
      if (!guide) return;
      topics.push({
        id: `guide.${sectionKey}.${def.res}`,
        tab: "guide",
        section: sectionKey,
        area: def.res,
        resource: def.res,
        title: guide.title,
        summary: guide.what || "",
        body: formatGuideArticle(guide),
        url: buildHelpUrl({ tab: "guide", section: sectionKey, area: def.res }),
        segments: Array.isArray(def.segments) ? def.segments : [],
      });
    });
  };

  pushSection("main", helpNavMainKeys);
  pushSection("settings", helpNavSettingsKeys);

  Object.entries(guideContentByKey.topbar || {}).forEach(([key, guide]) => {
    topics.push({
      id: `guide.topbar.${key}`,
      tab: "guide",
      section: "topbar",
      area: key,
      title: guide.title,
      summary: guide.what || "",
      body: formatGuideArticle(guide),
      url: buildHelpUrl({ tab: "guide", section: "topbar", area: key }),
      segments: [],
    });
  });

  Object.entries(guideContentByKey.platform || {}).forEach(([key, guide]) => {
    topics.push({
      id: `guide.platform.${key}`,
      tab: "guide",
      section: "platform",
      area: key,
      audience: "platform",
      title: guide.title,
      summary: guide.what || "",
      body: formatGuideArticle(guide),
      url: buildHelpUrl({ tab: "guide", section: "platform", area: key }),
      segments: [],
    });
  });

  if (guideContentByKey.partner?.overview) {
    const guide = guideContentByKey.partner.overview;
    topics.push({
      id: "guide.partner.overview",
      tab: "guide",
      section: "partner",
      area: "overview",
      audience: "partner",
      title: guide.title,
      summary: guide.what || "",
      body: formatGuideArticle(guide),
      url: buildHelpUrl({ tab: "guide", section: "partner", area: "overview" }),
      segments: [],
    });
  }

  topics.push(...channelTypeTopicsForSegment(segment));
  topics.push(...chatScreenTopicsForSegment(segment));

  return topics;
}

function chatScreenTopicsForSegment(segment) {
  const seg = normalizeWorkspaceSegment(segment);
  const topics = [];

  topics.push({
    id: "guide.main.chat.overview",
    tab: "guide",
    section: "main",
    area: "chat",
    resource: "chat",
    title: CHAT_SCREEN_OVERVIEW.title,
    summary: CHAT_SCREEN_OVERVIEW.summary,
    url: CHAT_SCREEN_OVERVIEW.url,
    segments: [],
  });

  CHAT_AREAS.forEach((area) => {
    if (Array.isArray(area.segments) && area.segments.length && !area.segments.includes(seg)) return;
    if (Array.isArray(area.excludeSegments) && area.excludeSegments.includes(seg)) return;
    topics.push({
      id: `guide.main.chat.${area.id}`,
      tab: "guide",
      section: "main",
      area: "chat",
      resource: "chat",
      title: area.title,
      summary: [area.summary, area.body].filter(Boolean).join(" "),
      url: area.url || "/help?area=chat",
      segments: area.segments || [],
      excludeSegments: area.excludeSegments || [],
    });
  });

  CHAT_WORKFLOWS.forEach((wf) => {
    const stepsText = Array.isArray(wf.steps) ? wf.steps.join(" ") : "";
    topics.push({
      id: `guide.main.chat.${wf.id}`,
      tab: "guide",
      section: "main",
      area: "chat",
      resource: "chat",
      title: wf.title,
      summary: [wf.summary, stepsText].filter(Boolean).join(" "),
      url: wf.url || "/help?area=chat",
      segments: wf.segments || [],
      excludeSegments: wf.excludeSegments || [],
    });
  });

  return topics;
}

function channelTypeTopicsForSegment(segment) {
  const seg = normalizeWorkspaceSegment(segment);
  const topics = [];

  topics.push({
    id: "guide.settings.channels.routing",
    tab: "guide",
    section: "settings",
    area: "channels",
    resource: "channels",
    title: CHANNEL_ROUTING.title,
    summary: CHANNEL_ROUTING.summary,
    url: CHANNEL_ROUTING.url,
    segments: [],
  });

  CHANNEL_TYPES.forEach((ch) => {
    if (Array.isArray(ch.segments) && ch.segments.length && !ch.segments.includes(seg)) return;
    if (Array.isArray(ch.excludeSegments) && ch.excludeSegments.includes(seg)) return;
    const setupText = Array.isArray(ch.setup) ? ch.setup.join(" ") : "";
    topics.push({
      id: `guide.settings.channels.type.${ch.key}`,
      tab: "guide",
      section: "settings",
      area: "channels",
      resource: "channels",
      title: `Canal: ${ch.label}`,
      summary: [ch.summary, setupText].filter(Boolean).join(" "),
      url: buildHelpUrl({ tab: "guide", section: "settings", area: "channels" }),
      segments: ch.segments || [],
      excludeSegments: ch.excludeSegments || [],
    });
  });

  PLATFORM_WORKFLOWS.forEach((wf) => {
    const stepsText = Array.isArray(wf.steps) ? wf.steps.join(" ") : "";
    topics.push({
      id: `guide.workflow.${wf.id}`,
      tab: "guide",
      section: "settings",
      area: wf.id.includes("agent") ? "agents" : "channels",
      resource: "channels",
      title: wf.title,
      summary: [wf.summary, stepsText].filter(Boolean).join(" "),
      url: wf.url || buildHelpUrl({ tab: "guide", section: "settings", area: "channels" }),
      segments: wf.segments || [],
      excludeSegments: wf.excludeSegments || [],
    });
  });

  return topics;
}

function faqTopicsForSegment(segment) {
  const seg = normalizeWorkspaceSegment(segment);
  const topics = [];
  faqCategories.forEach((cat) => {
    cat.items.forEach((item, itemIdx) => {
      if (!faqItemMatchesSegment(item, seg)) return;
      topics.push({
        id: `faq.${cat.key}.${itemIdx}`,
        tab: "faq",
        faqCat: cat.key,
        faqItem: itemIdx,
        audience: item.audience || cat.audience || "",
        requiredPermission: item.requiredPermission || null,
        title: item.q,
        summary: item.a,
        url: buildHelpUrl({ tab: "faq", faqCat: cat.key, faqItem: itemIdx }),
        segments: item.segments || [],
        excludeSegments: item.excludeSegments || [],
      });
    });
  });
  return topics;
}

/** Índice completo (todas as combinações de segmento conhecidas). */
export function buildHelpCenterIndex() {
  const segments = Object.values(WorkspaceSegments);
  const bySegment = {};
  segments.forEach((segment) => {
    bySegment[segment] = {
      guide: guideTopicsForSegment(segment),
      faq: faqTopicsForSegment(segment),
    };
  });
  return {
    version: 1,
    generatedAt: new Date().toISOString(),
    segments,
    bySegment,
  };
}

/** Filtra tópicos do índice conforme segmento e perfil (access.scopeSet = Set). */
function topicMatchesAccessForIndex(topic, access) {
  if (!access) return true;
  const audience = String(topic?.audience || "").trim();
  if (audience === "platform") return Boolean(access.isPlatformAdmin);
  if (audience === "partner") return Boolean(access.isPartner);
  if (audience === "billing") return Boolean(access.canViewBilling || access.isMaster);

  if (Array.isArray(topic?.requiredPermission) && topic.requiredPermission.length >= 1) {
    const [resource, action = "view"] = topic.requiredPermission;
    if (access.isMaster) return true;
    return access.scopeSet?.has(`${resource}.${action}`);
  }

  if (topic?.resource) {
    if (access.isMaster) return true;
    return access.scopeSet?.has(`${topic.resource}.view`);
  }
  return true;
}

export function filterHelpCenterTopics(topics, { segment, access } = {}) {
  const seg = normalizeWorkspaceSegment(segment);
  return (topics || []).filter((topic) => {
    if (topic.segments?.length && !topic.segments.map(normalizeWorkspaceSegment).includes(seg)) return false;
    if (topic.excludeSegments?.length && topic.excludeSegments.map(normalizeWorkspaceSegment).includes(seg)) {
      return false;
    }
    if (!topicMatchesAccessForIndex(topic, access)) return false;
    return true;
  });
}

/** Texto compacto para prompts de IA (~ segmento + perfil). */
export function buildHelpCenterPlainText(index, segment, { maxTopics = 40, maxChars = 12000, access = null } = {}) {
  const seg = normalizeWorkspaceSegment(segment);
  const bucket = index?.bySegment?.[seg] || index?.bySegment?.[WorkspaceSegments.Generic];
  if (!bucket) return "";

  const guide = filterHelpCenterTopics(bucket.guide || [], { segment: seg, access });
  const faq = filterHelpCenterTopics(bucket.faq || [], { segment: seg, access });

  const lines = [
    "Central de Ajuda Xpro4 (conteúdo estático — use links relativos /help?... para aprofundar):",
    `Segmento do workspace: ${seg}`,
    "",
    "=== Guia por área ===",
  ];

  guide.slice(0, maxTopics).forEach((t) => {
    lines.push(`- ${t.title}: ${String(t.summary || "").slice(0, 220)} → ${t.url}`);
  });

  lines.push("", "=== Perguntas frequentes ===");
  faq.slice(0, maxTopics).forEach((t) => {
    lines.push(`- ${t.title}: ${String(t.summary || "").slice(0, 180)} → ${t.url}`);
  });

  lines.push(
    "",
    "Instrução: ao explicar como usar a plataforma, cite 1–3 links acima quando o usuário puder se aprofundar."
  );

  const text = lines.join("\n");
  return text.length > maxChars ? `${text.slice(0, maxChars)}…` : text;
}
